import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { wishlistApi } from '../api/endpoints';
import { useAuth } from './AuthContext';

const WishlistContext = createContext(null);
const WISH_KEY = 'nv_wishlist';

function readLocal() {
  try {
    return JSON.parse(localStorage.getItem(WISH_KEY) || '[]');
  } catch {
    return [];
  }
}

function writeLocal(items) {
  localStorage.setItem(WISH_KEY, JSON.stringify(items));
}

export function WishlistProvider({ children }) {
  const { isLoggedIn } = useAuth();
  const [items, setItems] = useState([]);

  const refresh = useCallback(async () => {
    if (isLoggedIn) {
      const res = await wishlistApi.get();
      setItems(res.data.data.products || []);
    } else {
      setItems(readLocal());
    }
  }, [isLoggedIn]);

  useEffect(() => {
    refresh().catch(() => setItems(isLoggedIn ? [] : readLocal()));
  }, [refresh, isLoggedIn]);

  const toggle = async (product) => {
    const id = product._id || product;
    const exists = items.some((p) => (p._id || p) === id);

    if (isLoggedIn) {
      if (exists) await wishlistApi.remove(id);
      else await wishlistApi.add(id);
      await refresh();
    } else {
      let next = readLocal();
      if (exists) next = next.filter((p) => (p._id || p) !== id);
      else next.push(typeof product === 'object' ? product : { _id: id });
      writeLocal(next);
      setItems(next);
    }
  };

  const has = useCallback(
    (productId) => items.some((p) => (p._id || p) === productId),
    [items]
  );

  const count = useMemo(() => items.length, [items]);

  return (
    <WishlistContext.Provider value={{ items, count, toggle, has, refresh }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider');
  return ctx;
}
